import { Info } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { useCandidateApprovalMessageQuery } from '@/modules/candidates/queries/useCandidateApprovalMessageQuery';
import { BookingLinkForm } from '@/modules/hiring-manager/components/BookingLinkForm';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import type { components } from '@/types/apiSchema';

type Blocker = components['schemas']['CandidateApprovalMessageBlocker'];

interface CandidateApprovalConfirmProps {
  jobId: string;
  candidateId: string;
  firstName: string;
  isApproving: boolean;
  /** `isSender` is whether the current user sends the invitation. */
  onApprove: (isSender: boolean) => void;
  onCancel: () => void;
}

/**
 * Confirms a Hiring Manager Approval. When Finlay can reach the candidate, it
 * shows the fixed invitation it sends on LinkedIn with the sender's booking
 * link; otherwise it says why and leaves the outreach to the hiring manager.
 * Either way this is where Finlay stops assisting.
 */
export function CandidateApprovalConfirm({
  jobId,
  candidateId,
  firstName,
  isApproving,
  onApprove,
  onCancel,
}: CandidateApprovalConfirmProps) {
  const previewQuery = useCandidateApprovalMessageQuery({ jobId, candidateId });
  const currentUser = useCurrentUserQuery().data;
  const preview = previewQuery.data;
  const isSender =
    preview?.senderUserId != null && preview.senderUserId === currentUser?.id;
  const willSend = Boolean(preview?.content);

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        autoFocus
        className="h-8 rounded-md"
        disabled={previewQuery.isPending}
        loading={isApproving}
        onClick={() => onApprove(isSender)}
        size="sm"
      >
        {willSend
          ? 'Approve & send invitation'
          : previewQuery.isError
            ? 'Approve'
            : 'Approve without invitation'}
      </Button>
      <Button
        className="h-8 rounded-md"
        disabled={isApproving}
        onClick={onCancel}
        size="sm"
        variant="ghost"
      >
        Cancel
      </Button>
    </div>
  );

  if (previewQuery.isPending) {
    return (
      <Panel>
        <Skeleton className="h-24 w-full" />
        {actions}
      </Panel>
    );
  }

  if (previewQuery.isError || !preview) {
    return (
      <Panel>
        <p className="text-muted-foreground text-xs">
          The invitation could not be loaded. You can still approve {firstName};
          if Finlay can reach them on LinkedIn, the invitation is sent.
        </p>
        {actions}
      </Panel>
    );
  }

  if (preview.content) {
    return (
      <Panel>
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium">
            Message to {firstName} on LinkedIn
          </p>
          <p className="rounded-md border bg-neutral-50 p-3 text-[13px] whitespace-pre-line">
            {preview.content}
          </p>
          <p className="text-muted-foreground text-xs">
            {isSender
              ? 'Sent from your LinkedIn, with your booking link.'
              : `Sent from ${preview.senderName ?? 'the job owner'}’s LinkedIn, with their booking link.`}
          </p>
        </div>
        <HandOffNote firstName={firstName} />
        {actions}
      </Panel>
    );
  }

  if (preview.blocker === 'NoBookingLink' && isSender) {
    return (
      <Panel>
        <p className="text-muted-foreground text-xs">
          Add your booking link so {firstName} can pick a time for the
          interview. Finlay sends it with your invitation on LinkedIn.
        </p>
        <BookingLinkForm
          submitLabel="Save link"
          onSaved={() => void previewQuery.refetch()}
        />
        <HandOffNote firstName={firstName} />
        {actions}
      </Panel>
    );
  }

  return (
    <Panel>
      <NoInvitationNote
        blocker={preview.blocker}
        bookingLink={currentUser?.meetingUrl ?? null}
        firstName={firstName}
        senderName={preview.senderName}
      />
      {actions}
    </Panel>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="animate-in fade-in flex w-full max-w-xl flex-col gap-3 duration-150 motion-reduce:animate-none">
      {children}
    </div>
  );
}

function HandOffNote({ firstName }: { firstName: string }) {
  return (
    <p className="text-muted-foreground flex gap-2 text-xs">
      <Info className="mt-px size-3.5 shrink-0" />
      <span>
        This is where Finlay stops assisting. Watch your LinkedIn inbox for{' '}
        {firstName}’s reply and your calendar for the booking; any further
        logistics are up to you.
      </span>
    </p>
  );
}

const blockerReason = (
  blocker: Blocker | null,
  firstName: string,
  senderName: string | null,
) => {
  switch (blocker) {
    case 'JobClosed':
      return 'this job is closed';
    case 'NoConversation':
      return `Finlay has no LinkedIn conversation with ${firstName} for this job`;
    case 'SenderUnavailable':
      return `the LinkedIn account that messaged ${firstName} is disconnected or can no longer send messages`;
    case 'NoBookingLink':
      return `${senderName ?? 'the person who messaged them'} has not added a booking link`;
    default:
      return `Finlay cannot reach ${firstName} on LinkedIn`;
  }
};

function NoInvitationNote({
  blocker,
  bookingLink,
  firstName,
  senderName,
}: {
  blocker: Blocker | null;
  bookingLink: string | null;
  firstName: string;
  senderName: string | null;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (!bookingLink) return;
    void navigator.clipboard.writeText(bookingLink).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="text-muted-foreground flex gap-2 text-xs">
      <Info className="mt-px size-3.5 shrink-0" />
      <div className="flex min-w-0 flex-col gap-2">
        <p>
          Finlay can’t send {firstName} an invitation:{' '}
          {blockerReason(blocker, firstName, senderName)}. Approve them and
          contact them yourself to arrange the interview.
        </p>
        {bookingLink && (
          <p className="flex flex-wrap items-center gap-2">
            <span>Your booking link:</span>
            <span className="text-foreground break-all">{bookingLink}</span>
            <Button
              className="h-6 rounded-md px-2 text-xs"
              onClick={copy}
              size="sm"
              type="button"
              variant="outline"
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </p>
        )}
      </div>
    </div>
  );
}
